import type { RootState } from 'src/redux/store';
import { useSelector } from 'src/redux/store';

export type PermissionOptions = {
  // An existing USER_APP_PERMISSIONS key that covers the same capability
  // under the old system - used only when featureKey has no entry at all.
  fallbackAppPermKey?: string;
  // Value to use when featureKey has no entry in userFeaturePermissions AND
  // no fallbackAppPermKey is given (or it also has no entry). Defaults to
  // true so features with no prior gating stay available for users who
  // haven't been assigned a User Role yet - rollout safety, not a security
  // boundary change. Pass false to opt a check into strict deny-by-default.
  defaultValue?: boolean;
};

export type PermissionMaps = Pick<
  RootState['data'],
  'userAppPermissions' | 'userFeaturePermissions'
>;

// Single source of truth for reading either permission map. A key present in
// userFeaturePermissions always wins over userAppPermissions/defaultValue -
// this is what lets a feature permission supersede an app permission (or a
// previously-ungated feature) without every call site re-deriving priority.
// Pure, so code outside React (registries, send-time payload builders) can
// apply the exact same rule through a resolved `isGranted` function.
export const resolvePermission = (
  { userAppPermissions, userFeaturePermissions }: PermissionMaps,
  featureKey: string,
  options: PermissionOptions = {}
): boolean => {
  const featureValue = userFeaturePermissions?.[featureKey]?.value;
  if (featureValue !== undefined) {
    return featureValue;
  }

  if (options.fallbackAppPermKey) {
    return !!userAppPermissions?.[options.fallbackAppPermKey]?.value;
  }

  return options.defaultValue ?? true;
};

export const usePermission = (
  featureKey: string,
  options: PermissionOptions = {}
): boolean => {
  const { userAppPermissions, userFeaturePermissions } = useSelector(
    (state: RootState) => state.data
  );
  return resolvePermission(
    { userAppPermissions, userFeaturePermissions },
    featureKey,
    options
  );
};

export default usePermission;
